import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Activity } from 'src/app/model/options/activity';
import { Service } from 'src/app/model/options/service';
import {
  comparisonToLogicalString,
  ExpressionComparison,
} from 'src/app/model/search-filters/expression-comparison';
import {
  ExpressionField,
  fieldToLogicalString,
} from 'src/app/model/search-filters/expression-field';
import { ExpressionRule } from 'src/app/model/search-filters/expression-rule';
import { SelectOption } from 'src/app/model/select-option';
import { StudioType } from 'src/app/model/studio-type';
import options from '../../../../../assets/options.json';

@Component({
  selector: 'gymfinder-expression',
  templateUrl: './expression.component.html',
  styleUrls: ['./expression.component.scss'],
  standalone: false,
})
export class ExpressionComponent implements OnInit {
  @Input()
  expressionRule: ExpressionRule;

  @Output()
  expressionRuleChange = new EventEmitter<ExpressionRule>();

  @Input()
  studioTypes: StudioType[];

  @Input()
  services: Service[];

  @Input()
  activities: Activity[];

  @Input()
  gymEquipments: string[];

  @Input()
  gymInfos: string[];

  @Input()
  gymTypes: string[];

  @Input()
  equipmentsToBring: string[];

  @Input()
  networks: string[];

  @Input()
  geniusFeatures: string[];

  @Output()
  expressionRuleRemoved = new EventEmitter<void>();

  fieldValues: SelectOption[];

  valueValues: SelectOption[];

  studioTypesValues: SelectOption[] = [];

  activitiesValues: SelectOption[] = [];

  servicesValues: SelectOption[] = [];

  gymEquipmentsValues: SelectOption[] = [];

  gymInfosValues: SelectOption[] = [];

  gymTypesValues: SelectOption[] = [];

  equipmentsToBringValues: SelectOption[] = [];

  networkValues: SelectOption[] = [];

  geniusFeaturesValues: SelectOption[] = [];

  private _field?: ExpressionField;

  private _comparison?: ExpressionComparison;

  private _value?: string;

  ngOnInit(): void {
    this.initValues();

    this._field = this.expressionRule.field;
    this._comparison = this.expressionRule.comparison;
    this._value = this.expressionRule.value;

    this.changeSelectValues();
  }

  get field(): ExpressionField | undefined {
    return this._field;
  }

  set field(newValue: ExpressionField | undefined) {
    this._field = newValue;
    this._value = undefined;
    this.expressionRule.field = newValue;
    this.expressionRule.value = undefined;

    this.changeSelectValues();

    this.expressionRuleChange.emit(this.expressionRule);
  }

  get comparison(): ExpressionComparison | undefined {
    return this._comparison;
  }

  get value(): string | undefined {
    return this._value;
  }

  set value(newValue: string | undefined) {
    this._value = newValue;
    this.expressionRule.value = newValue;
    this.expressionRuleChange.emit(this.expressionRule);
  }

  private initValues() {
    this.fieldValues = Object.values(ExpressionField)
      .sort((a, b) => a.localeCompare(b))
      .map(k => ({ key: k, value: fieldToLogicalString(k) }));

    this.studioTypesValues = this.valueValues = (this.studioTypes || [])
      .sort((a, b) => a.value.localeCompare(b.value))
      .map(v => ({ key: v.name, value: v.value }));

    this.activitiesValues = this.valueValues = (this.activities || [])
      .sort((a, b) => a.activity.localeCompare(b.activity))
      .map(v => ({
        key: v.activity,
        value:
          (options.activities as { [key: string]: string })[v.activity] ||
          v.activity,
      }));

    this.servicesValues = this.valueValues = (this.services || [])
      .sort((a, b) => a.service.localeCompare(b.service))
      .map(v => ({
        key: v.service,
        value:
          (options.services as { [key: string]: string })[v.service] ||
          v.service,
      }));

    this.equipmentsToBringValues = this.valueValues = (
      this.equipmentsToBring || []
    )
      .sort((a, b) => a.localeCompare(b))
      .map(v => ({
        key: v,
        value: (options.equipmentsToBring as { [key: string]: string })[v] || v,
      }));

    this.gymEquipmentsValues = this.valueValues = (this.gymEquipments || [])
      .sort((a, b) => a.localeCompare(b))
      .map(v => ({
        key: v,
        value: (options.gymEquipments as { [key: string]: string })[v] || v,
      }));

    this.gymInfosValues = this.valueValues = (this.gymInfos || [])
      .sort((a, b) => a.localeCompare(b))
      .map(v => ({
        key: v,
        value: (options.gymInfos as { [key: string]: string })[v] || v,
      }));

    this.gymTypesValues = this.valueValues = (this.gymTypes || [])
      .sort((a, b) => a.localeCompare(b))
      .map(v => ({
        key: v,
        value: (options.gymTypes as { [key: string]: string })[v] || v,
      }));

    this.networkValues = this.valueValues = (this.networks || [])
      .sort((a, b) => a.localeCompare(b))
      .map(v => ({
        key: v,
        value: (options.networks as { [key: string]: string })[v] || v,
      }));

    this.geniusFeaturesValues = this.valueValues = (
      this.geniusFeatures || []
    ).map(v => ({
      key: v,
      value: (options.geniusFeatures as { [key: string]: string })[v] || v,
    }));
  }

  private changeSelectValues() {
    if (this.studioTypes) {
      if (this.field === ExpressionField.STUDIO_TYPE) {
        this.valueValues = this.studioTypesValues;
      } else if (this.field === ExpressionField.ACTIVITY) {
        this.valueValues = this.activitiesValues;
      } else if (this.field === ExpressionField.SERVICE) {
        this.valueValues = this.servicesValues;
      } else if (this.field === ExpressionField.GYM_EQUIPMENT) {
        this.valueValues = this.gymEquipmentsValues;
      } else if (this.field === ExpressionField.GYM_INFO) {
        this.valueValues = this.gymInfosValues;
      } else if (this.field === ExpressionField.GYM_TYPE) {
        this.valueValues = this.gymTypesValues;
      } else if (this.field === ExpressionField.EQUIPMENT_TO_BRING) {
        this.valueValues = this.equipmentsToBringValues;
      } else if (this.field === ExpressionField.NETWORK) {
        this.valueValues = this.networkValues;
      } else if (this.field === ExpressionField.GENIUS_FEATURES) {
        this.valueValues = this.geniusFeaturesValues;
      } else {
        this.valueValues = [];
      }
    }
  }

  public getComparisonString(comparison?: ExpressionComparison): string {
    return comparisonToLogicalString(comparison);
  }

  public onExpressionRuleRemoved() {
    this.expressionRuleRemoved.emit();
  }
}
